import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { toast } from "@/components/podkit/toasts/use-toast";
import type { PlainServiceAccount } from "@/queries/service-account-queries";
import { serviceAccountDetailsUrl } from "@/utils/service-accounts";
import IconTasksBase from "central-icons/IconTasks";
import IconPencilBase from "central-icons/IconPencil";
import IconTrashCanBase from "central-icons/IconTrashCan";
import IconSquareBehindSquare2Base from "central-icons/IconSquareBehindSquare2";
import { withGitpodSize } from "@/assets/icons/utils";

const IconTasks = withGitpodSize(IconTasksBase);
const IconPencil = withGitpodSize(IconPencilBase);
const IconTrashCan = withGitpodSize(IconTrashCanBase);
const IconSquareBehindSquare = withGitpodSize(IconSquareBehindSquare2Base);

type ServiceAccountActionsDropdownProps = {
    serviceAccount: PlainServiceAccount;
    onEdit: () => void;
    onDelete: () => void;
};

export const ServiceAccountActionsDropdown: FC<ServiceAccountActionsDropdownProps> = ({
    serviceAccount,
    onEdit,
    onDelete,
}) => {
    const navigate = useNavigate();

    const handleViewDetails = useCallback(() => {
        navigate(serviceAccountDetailsUrl({ id: serviceAccount.id }));
    }, [navigate, serviceAccount.id]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(serviceAccount.id);
        toast({
            title: "Service account ID copied to clipboard",
            description: serviceAccount.id,
        });
    }, [serviceAccount.id]);

    return (
        <DropdownActions triggerTestId={`service-account-actions-${serviceAccount.id}`}>
            <DropdownMenuItem
                onClick={handleViewDetails}
                data-tracking-id="view-service-account-details"
                LeadingIcon={IconTasks}
            >
                View details
            </DropdownMenuItem>
            <DropdownMenuItem
                onClick={handleCopyId}
                data-tracking-id="copy-service-account-id"
                LeadingIcon={IconSquareBehindSquare}
            >
                Copy ID
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit} data-tracking-id="edit-service-account" LeadingIcon={IconPencil}>
                Edit
            </DropdownMenuItem>
            <DropdownMenuItem
                onClick={onDelete}
                data-tracking-id="delete-service-account"
                variant="destructive"
                LeadingIcon={IconTrashCan}
            >
                Delete
            </DropdownMenuItem>
        </DropdownActions>
    );
};
